/**
 * Composable for viewport-based lazy loading
 *
 * This composable provides intersection observer functionality to only load
 * content when it's visible or near the viewport. Perfect for large lists
 * of thumbnails or images.
 */

import { ref, onMounted, onUnmounted, type Ref } from 'vue'

interface UseViewportLoadingOptions {
  /**
   * Root margin for intersection observer
   * Controls how early to start loading (e.g., "200px" loads 200px before visible)
   */
  rootMargin?: string

  /**
   * Threshold for intersection observer
   * 0 = as soon as any pixel is visible
   * 1 = only when fully visible
   */
  threshold?: number

  /**
   * Whether to only observe once (stop observing after first intersection)
   */
  once?: boolean
}

interface UseViewportLoadingReturn {
  /**
   * Whether the element is currently visible in viewport
   */
  isVisible: Ref<boolean>

  /**
   * Whether the element has ever been visible (useful for "once" mode)
   */
  hasBeenVisible: Ref<boolean>

  /**
   * Target element to observe
   */
  targetRef: Ref<HTMLElement | null>

  /**
   * Manually trigger visibility (useful for testing or edge cases)
   */
  triggerVisibility: () => void
}

export function useViewportLoading(
  options: UseViewportLoadingOptions = {}
): UseViewportLoadingReturn {
  const {
    rootMargin = '200px', // Load 200px before visible by default
    threshold = 0,
    once = true // By default, only load once
  } = options

  const isVisible = ref(false)
  const hasBeenVisible = ref(false)
  const targetRef = ref<HTMLElement | null>(null)

  let observer: IntersectionObserver | null = null

  const triggerVisibility = () => {
    isVisible.value = true
    hasBeenVisible.value = true
  }

  const setupObserver = () => {
    if (!window.IntersectionObserver) {
      // Fallback for browsers without IntersectionObserver
      triggerVisibility()
      return
    }

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisible.value = entry.isIntersecting

          if (entry.isIntersecting) {
            hasBeenVisible.value = true

            // If "once" mode, stop observing after first intersection
            if (once && observer && targetRef.value) {
              observer.unobserve(targetRef.value)
            }
          }
        })
      },
      {
        rootMargin,
        threshold
      }
    )

    if (targetRef.value) {
      observer.observe(targetRef.value)
    }
  }

  onMounted(() => {
    setupObserver()
  })

  onUnmounted(() => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
  })

  // Custom setter to handle re-observing when target changes
  const setTargetRef = (element: HTMLElement | null) => {
    // Unobserve old element
    if (observer && targetRef.value) {
      observer.unobserve(targetRef.value)
    }

    // Update ref
    targetRef.value = element

    // Observe new element
    if (observer && element) {
      observer.observe(element)
    }
  }

  // Create a proxy ref with custom setter behavior
  const proxyTargetRef = {
    get value() {
      return targetRef.value
    },
    set value(element: HTMLElement | null) {
      setTargetRef(element)
    }
  } as Ref<HTMLElement | null>

  return {
    isVisible,
    hasBeenVisible,
    targetRef: proxyTargetRef,
    triggerVisibility
  }
}

/**
 * Simpler viewport loading hook for cases where you just need "has been visible"
 */
export function useViewportLoadingOnce(rootMargin = '200px') {
  return useViewportLoading({
    rootMargin,
    threshold: 0,
    once: true
  })
}

/**
 * Viewport loading hook for continuous monitoring
 */
export function useViewportLoadingContinuous(rootMargin = '100px') {
  return useViewportLoading({
    rootMargin,
    threshold: 0,
    once: false
  })
}
